import React, {useState, useEffect} from 'react';
import {
    AlertCircle,
    CheckCircle,
    Upload,
    Plus,
    Users,
    Trash2,
    Edit2,
    Eye,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Printer,
    Phone,
    Mail,
} from 'lucide-react';
import LabelPrintModal from '../components/Labels/LabelPrintModal';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import secureStorage from '../utils/secureStorage';
import {api} from '../config/api';
import {useNavigate} from 'react-router-dom';
import ClientImportExportModal from './ClientImportExportModal';
import Button from '../components/Common/Button';
import {formatPhone, phoneHref} from '../utils/phone';
import {
    HeaderAction, SearchInput, ActiveFilters, ListLoader, ListEmpty, ListCard, thClass, mobileListClass, mobileRowClass, Avatar, RowActions, Pagination
} from '../stock/StockUI';
import Notification from "../components/Common/Notification";

/* ─── Sortable Header ───────────────────────────────────────────────── */
const SortableHeader = ({field, currentField, direction, onSort, children}) => {
    const isActive = currentField === field;
    return (
        <th
            className={`group ${thClass} cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100 transition-colors`}
            onClick={() => onSort(field)}
        >
            <div className="flex items-center gap-1.5">
                <span>{children}</span>
                {isActive ? (
                    direction === 'asc'
                        ? <ArrowUp className="w-3.5 h-3.5 text-primary"/>
                        : <ArrowDown className="w-3.5 h-3.5 text-primary"/>
                ) : (
                    <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"/>
                )}
            </div>
        </th>
    );
};

/* ─── Helpers ───────────────────────────────────────────────────────── */
const getInitials = (f = '', l = '') => `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || '?';

const formatDate = (d) => {
    if (!d) return '—';
    return new Intl.DateTimeFormat('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric'}).format(new Date(d));
};

const CIVILITE_LABELS = {m: 'M.', mme: 'Mme', mlle: 'Mlle'};
const civiliteLabel = (c) => CIVILITE_LABELS[c] || '';

const clientName = (c) => {
    const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
    if (!name) return null;
    return c.civilite ? `${civiliteLabel(c.civilite)} ${name}` : name;
};

/* Click-to-call phone, formatted; plain dash when empty */
const PhoneCell = ({client, className = ''}) => {
    const href = phoneHref(client.countryCode, client.phone);
    if (!href) return <span className="text-gray-300 dark:text-gray-600">—</span>;
    return (
        <a
            href={href}
            onClick={e => e.stopPropagation()}
            className={`inline-flex items-center gap-1.5 tabular-nums text-gray-700 dark:text-gray-200 hover:text-primary transition-colors ${className}`}
        >
            <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"/>
            {formatPhone(client.countryCode, client.phone)}
        </a>
    );
};

const Dash = () => <span className="text-gray-300 dark:text-gray-600">—</span>;

const AVATAR_COLORS = [
    '#4DB6AC', '#7986CB', '#FF8A65', '#A5D6A7',
    '#F48FB1', '#FFD54F', '#80DEEA', '#CE93D8',
];
const avatarColor = (id = '') => {
    const n = parseInt(id.replace(/\D/g, '').slice(-1) || '0', 10);
    return AVATAR_COLORS[n % AVATAR_COLORS.length];
};

/* ─── Main Component ────────────────────────────────────────────────── */
const ClientsTable = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [selectedAll, setSelectedAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [sortField, setSortField] = useState('lastName');
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [notification, setNotification] = useState({show: false, type: 'success', message: ''});
    const [isImportExportModalVisible, setImportExportModalVisible] = useState(false);
    const [labelItems, setLabelItems] = useState([]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideDown { from {opacity:0; transform:translateY(-20px);} to {opacity:1; transform:translateY(0);} }
            @keyframes fadeIn    { from {opacity:0;} to {opacity:1;} }
            @keyframes scaleIn   { from {opacity:0; transform:scale(.95);} to {opacity:1; transform:scale(1);} }
            .selection-bar-appear { animation: slideDown .3s ease-out forwards; }
            .modal-appear         { animation: fadeIn .2s ease-out forwards; }
            .card-appear          { animation: scaleIn .2s ease-out forwards; }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearchTerm(searchTerm); setCurrentPage(1); }, 600);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // Puce « Filtres actifs » : retire la recherche sans attendre le debounce
    const clearSearch = () => { setSearchTerm(''); setDebouncedSearchTerm(''); setCurrentPage(1); };

    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        if (type === 'success') setTimeout(() => setNotification(p => ({...p, show: false})), 4000);
    };
    const hideNotification = () => setNotification(p => ({...p, show: false}));

    const fetchClients = async () => {
        const accountId = await secureStorage.getItem('selectedAccount');
        let url = `/clients?page=${currentPage}`;
        if (debouncedSearchTerm.trim()) url += `&search=${encodeURIComponent(debouncedSearchTerm.trim())}`;
        if (sortField) url += `&order[${sortField}]=${sortDirection}`;
        const res = await api.get(url, {headers: {'X-account-id': accountId.id, Accept: 'application/json'}});
        if (res.data.pagination) {
            setTotalPages(res.data.pagination.totalPages);
            setTotalItems(res.data.pagination.total ?? res.data.pagination.totalItems ?? null);
            return res.data.data;
        }
        return res.data.data || res.data;
    };

    const {data: clients = [], isLoading, error} = useQuery({
        queryKey: ['clients', currentPage, debouncedSearchTerm, sortField, sortDirection],
        queryFn: fetchClients,
        keepPreviousData: true,
    });

    useEffect(() => {
        if (selectedAll) setSelectedItems(clients.map(c => c.id));
        else setSelectedItems(prev => prev.filter(id => clients.some(c => c.id === id)));
    }, [clients, selectedAll]);

    const deleteMutation = useMutation({
        mutationFn: async (ids) => {
            const accountId = await secureStorage.getItem('selectedAccount');
            return Promise.all(ids.map(id => api.delete(`/clients/${id}`, {headers: {'X-account-id': accountId.id}})));
        },
        onMutate: () => showNotification('loading', 'Suppression en cours...'),
        onSuccess: () => {
            queryClient.invalidateQueries(['clients']);
            setSelectedItems([]); setSelectedAll(false);
            setShowConfirmDialog(false); setPendingDeleteIds([]);
            showNotification('success', 'Clients supprimés avec succès');
        },
        onError: (e) => {
            showNotification('error', `Erreur: ${e.message}`);
            setShowConfirmDialog(false); setPendingDeleteIds([]);
        },
    });

    const handleSelectAll = () => {
        const next = !selectedAll;
        setSelectedAll(next);
        setSelectedItems(next ? clients.map(c => c.id) : []);
    };
    const handleSelectItem = (id, e) => {
        e.stopPropagation();
        setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };
    const handleSort = (field) => {
        if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
        setCurrentPage(1);
    };
    const handleDeleteSingle = (id) => { setPendingDeleteIds([id]); setShowConfirmDialog(true); };
    const handleBulkDelete = () => {
        if (!selectedItems.length) return;
        setPendingDeleteIds(selectedItems);
        setShowConfirmDialog(true);
    };

    const handlePrintLabel = (client) => setLabelItems([client]);
    const handlePrintLabelsBulk = () => {
        const items = clients.filter(c => selectedItems.includes(c.id));
        if (items.length) setLabelItems(items);
    };

    const handleClientImport = async (file) => {
        const accountId = await secureStorage.getItem('selectedAccount');
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await api.post('/clients/import', formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Content-Type': 'multipart/form-data',
                    'Accept': 'application/json'
                }
            });

            if (res.status === 200) {
                queryClient.invalidateQueries(['clients']);

                // Afficher les statistiques d'import
                const stats = res.data.stats || {};
                const message = res.data.message ||
                    `Import terminé : ${stats.added || 0} ajouté(s), ${stats.updated || 0} mis à jour, ${stats.skipped || 0} ignoré(s).`;

                showNotification('success', message);

                // Si des erreurs sont présentes, les afficher
                if (res.data.errors && res.data.errors.length > 0) {
                    console.warn('Erreurs lors de l\'import:', res.data.errors);
                }

                return res.data;
            }

            const errorMsg = "Erreur lors de l'importation";
            showNotification('error', errorMsg);
            throw new Error(errorMsg);
        } catch (error) {
            const errorMsg = error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                "Erreur lors de l'importation";
            showNotification('error', errorMsg);
            throw error;
        }
    };

    const handleClientExport = async (isTemplate = false) => {
        const accountId = await secureStorage.getItem('selectedAccount');
        const res = await api.get(isTemplate ? '/clients/template' : '/clients/export', {headers: {'X-account-id': accountId.id}, responseType: 'blob'});
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', isTemplate ? 'modele_clients.xlsx' : `clients_export_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const total = totalItems ?? clients.length;
    const rowActions = (c) => [
        {label: 'Modifier', icon: Edit2, onClick: () => navigate(`/clients/edit/${c.id}`)},
        {label: 'Détails', icon: Eye, tone: 'green', onClick: () => navigate(`/clients/edit/${c.id}`)},
        {label: 'Imprimer étiquette', icon: Printer, tone: 'green', onClick: () => handlePrintLabel(c)},
        {label: 'Supprimer', icon: Trash2, tone: 'red', danger: true, onClick: () => handleDeleteSingle(c.id)},
    ];
    const ClientAvatar = ({client, size}) => (
        <Avatar name={getInitials(client.firstName, client.lastName)} color={avatarColor(client.id)} size={size}/>
    );

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">

            {/* ── Header — same layout as the catalogue page ── */}
            <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <div className="px-4 py-4 sm:px-8">
                    <div className="page-header-row">
                        <h1 className="page-title">Clients</h1>
                        <div className="flex items-center gap-1 sm:gap-2">
                            <HeaderAction icon={Upload} label="Importer" onClick={() => setImportExportModalVisible(true)}/>
                            <HeaderAction icon={Plus} label="Nouveau" onClick={() => navigate('/clients/new')}/>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-full">
            {/* ── Search ── */}
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher un client (nom, téléphone, email)..."/>
            </div>
            <ActiveFilters
                chips={debouncedSearchTerm.trim() ? [{key: 'search', label: `Recherche: ${debouncedSearchTerm.trim()}`, onRemove: clearSearch}] : []}
                onClearAll={clearSearch}
            />

            {/* ── Selection Bar ── */}
            {selectedItems.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-primary/10 dark:bg-primary/20 border-b border-primary/20 selection-bar-appear">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-full shadow">
                            <CheckCircle className="w-4 h-4 text-white"/>
                        </div>
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {selectedItems.length} client{selectedItems.length > 1 ? 's' : ''} sélectionné{selectedItems.length > 1 ? 's' : ''}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="tinted" size="sm" icon={Printer} compact onClick={handlePrintLabelsBulk}>
                            Étiquettes
                        </Button>
                        <Button variant="danger" size="sm" icon={Trash2} compact onClick={handleBulkDelete}>
                            Supprimer
                        </Button>
                    </div>
                </div>
            )}

            {/* ── Table ── */}
            {isLoading ? (
                <ListLoader label="Chargement des clients..."/>
            ) : error ? (
                <div className="flex flex-col items-center justify-center h-40 gap-2">
                    <AlertCircle className="w-8 h-8 text-red-500"/>
                    <p className="text-sm text-red-500">{error.message}</p>
                </div>
            ) : (
            <ListCard>
                {clients.length === 0 ? (
                    <ListEmpty icon={Users} title="Aucun client trouvé"
                               text="Ajustez la recherche pour retrouver vos clients."/>
                ) : (
                    <>
                        {/* Desktop Table */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className="w-12 pl-6 pr-2 py-4 text-left">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            checked={selectedAll}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <SortableHeader field="lastName" currentField={sortField} direction={sortDirection} onSort={handleSort}>Client</SortableHeader>
                                    <SortableHeader field="phone" currentField={sortField} direction={sortDirection} onSort={handleSort}>Téléphone</SortableHeader>
                                    <SortableHeader field="city" currentField={sortField} direction={sortDirection} onSort={handleSort}>Ville</SortableHeader>
                                    <th className={thClass}>Type</th>
                                    <SortableHeader field="birthday" currentField={sortField} direction={sortDirection} onSort={handleSort}>Date de naissance</SortableHeader>
                                    <th className={`${thClass} text-right`}>Actions</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {clients.map(c => (
                                    <tr
                                        key={c.id}
                                        className={`transition-colors cursor-pointer ${
                                            selectedItems.includes(c.id)
                                                ? 'bg-primary/5 dark:bg-primary/10'
                                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                        onClick={() => navigate(`/clients/edit/${c.id}`)}
                                    >
                                        <td className="pl-6 pr-2 py-4">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                checked={selectedItems.includes(c.id)}
                                                onChange={e => handleSelectItem(c.id, e)}
                                                onClick={e => e.stopPropagation()}
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <ClientAvatar client={c}/>
                                                <div className="min-w-0">
                                                    <div className={`text-sm font-semibold truncate max-w-xs ${clientName(c) ? 'text-gray-900 dark:text-gray-100' : 'italic text-gray-400'}`}>
                                                        {clientName(c) || 'Sans nom'}
                                                    </div>
                                                    {c.email && (
                                                        <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                                            <Mail className="w-3 h-3 flex-shrink-0"/>
                                                            {c.email}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                                            <PhoneCell client={c}/>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                                            {c.city || <Dash/>}
                                        </td>
                                        <td className="px-6 py-4">
                                            {c.clientType?.name ? (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary">
                                                    {c.clientType.name}
                                                </span>
                                            ) : <Dash/>}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 tabular-nums whitespace-nowrap">
                                            {c.birthday ? formatDate(c.birthday) : <Dash/>}
                                        </td>
                                        <td className="px-6 py-4 text-right" onClick={e => e.stopPropagation()}>
                                            <RowActions actions={rowActions(c)}/>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile rows — edge to edge, like the Stock / catalogue lists */}
                        <div className={mobileListClass}>
                            {clients.map(c => (
                                <div
                                    key={c.id}
                                    className={`${mobileRowClass} cursor-pointer ${selectedItems.includes(c.id) ? '!bg-primary/5 dark:!bg-primary/10' : ''}`}
                                    onClick={() => navigate(`/clients/edit/${c.id}`)}
                                >
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            checked={selectedItems.includes(c.id)}
                                            onChange={e => handleSelectItem(c.id, e)}
                                            onClick={e => e.stopPropagation()}
                                        />
                                        <ClientAvatar client={c} size="lg"/>
                                        <div className="flex-1 min-w-0">
                                            <h3 className={`text-base font-semibold truncate ${clientName(c) ? 'text-gray-900 dark:text-gray-100' : 'italic text-gray-400'}`}>
                                                {clientName(c) || 'Sans nom'}
                                            </h3>
                                            <div className="mt-0.5 text-xs">
                                                <PhoneCell client={c}/>
                                            </div>
                                            {c.email && (
                                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">{c.email}</p>
                                            )}
                                            <div className="flex flex-wrap items-center gap-1.5 mt-2 empty:hidden">
                                                {c.city && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                        {c.city}
                                                    </span>
                                                )}
                                                {c.clientType?.name && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary">
                                                        {c.clientType.name}
                                                    </span>
                                                )}
                                            </div>
                                            {c.birthday && (
                                                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                    Né(e) le {formatDate(c.birthday)}
                                                </p>
                                            )}
                                        </div>
                                        <div onClick={e => e.stopPropagation()}>
                                            <RowActions actions={rowActions(c)}/>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </ListCard>
            )}

            {/* ── Pagination ── */}
            {!isLoading && !error && clients.length > 0 && (
                <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    onPage={setCurrentPage}
                    summary={`${total} client${total > 1 ? 's' : ''}`}
                />
            )}
            </div>

            {/* ── Confirm Dialog ── */}
            {showConfirmDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm modal-appear">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full p-6">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Confirmer la suppression</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Supprimer {pendingDeleteIds.length} client{pendingDeleteIds.length > 1 ? 's' : ''} ? Action irréversible.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowConfirmDialog(false); setPendingDeleteIds([]); }}
                                className="btn-outline flex-1"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={() => deleteMutation.mutate(pendingDeleteIds)}
                                className="btn-danger flex-1"
                            >
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Notification ── */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />

            {/* ── Import/Export Modal ── */}
            <ClientImportExportModal
                isVisible={isImportExportModalVisible}
                onClose={() => setImportExportModalVisible(false)}
                onImport={handleClientImport}
                onExport={handleClientExport}
                isLoading={false}
            />

            {/* Label Print Modal */}
            <LabelPrintModal
                open={labelItems.length > 0}
                type="client"
                items={labelItems}
                onClose={() => setLabelItems([])}
            />
        </div>
    );
};

export default ClientsTable;
